import type { PublicVenue, PublicVenueBookingChannel } from './domain'

export function isExternalVenue(venue: PublicVenue) {
  return venue.bookingChannel?.kind === 'external'
}

export function isTrellisVenue(venue: PublicVenue) {
  return !isExternalVenue(venue)
}

/** Keep bookable Trellis inventory ahead of directory handoffs in mixed discovery lists. */
export function compareVenueNetwork(left: PublicVenue, right: PublicVenue) {
  return Number(isExternalVenue(left)) - Number(isExternalVenue(right))
}

export function externalBookingChannel(venue: PublicVenue) {
  return venue.bookingChannel?.kind === 'external' ? venue.bookingChannel : undefined
}

export function providerName(channel: Extract<PublicVenueBookingChannel, { kind: 'external' }>) {
  if (channel.provider === 'opentable') return 'OpenTable'
  if (channel.provider === 'resy') return 'Resy'
  if (channel.provider === 'tock') return 'Tock'
  return 'the restaurant'
}

export function externalActionLabel(channel: Extract<PublicVenueBookingChannel, { kind: 'external' }>) {
  if (channel.destination === 'website') return 'Visit restaurant website'
  return channel.provider === 'restaurant' || channel.provider === 'other'
    ? 'Reserve with the restaurant'
    : `Book on ${providerName(channel)}`
}

export function externalActionTitle(channel: Extract<PublicVenueBookingChannel, { kind: 'external' }>) {
  if (channel.destination === 'website') return 'Explore the restaurant'
  return channel.provider === 'restaurant' || channel.provider === 'other'
    ? 'Reserve with the restaurant'
    : `Book on ${providerName(channel)}`
}

export function externalActionDescription(channel: Extract<PublicVenueBookingChannel, { kind: 'external' }>) {
  if (channel.destination === 'website') return 'See current menus, hours, and reservation options on the restaurant’s official website.'
  return channel.provider === 'restaurant' || channel.provider === 'other'
    ? 'See current reservation options and complete your booking with the restaurant.'
    : `See current times and complete your reservation on ${providerName(channel)}.`
}

export function externalDestinationHost(channel: Extract<PublicVenueBookingChannel, { kind: 'external' }>) {
  try {
    return new URL(channel.url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

export function isSafeExternalDestination(channel: Extract<PublicVenueBookingChannel, { kind: 'external' }>) {
  try {
    const url = new URL(channel.url)
    if (url.protocol !== 'https:') return false
    const hostname = url.hostname.replace(/^www\./, '')
    if (channel.provider === 'resy') return hostname === 'resy.com'
    if (channel.provider === 'opentable') return hostname === 'opentable.com'
    if (channel.provider === 'tock') return hostname === 'exploretock.com'
    return true
  } catch {
    return false
  }
}
